import React, { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  ZoomOut, 
  Download,
  RotateCw,
  Maximize,
  FileText,
  AlertCircle,
  Loader
} from 'lucide-react';
import { PDF_VIEWER_CONFIG } from '../../constants/pdfViewer';
import { useAuth0 } from '@auth0/auth0-react';

// Set up PDF.js worker - use local worker file from public directory
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface PDFViewerProps {
  url: string;
  filename?: string;
  className?: string;
  enableAnnotations?: boolean; // Performance gate for annotation layer
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ 
  url, 
  filename = 'document.pdf',
  className = '',
  enableAnnotations = PDF_VIEWER_CONFIG.ENABLE_ANNOTATIONS
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(PDF_VIEWER_CONFIG.DEFAULT_SCALE);
  const [rotation, setRotation] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((error: any) => {
    console.error('PDF load error:', error);
    setError('Failed to load PDF. Please check if the file exists and is accessible.');
    setIsLoading(false);
  }, []);

  const goToPrevPage = useCallback(() => {
    setPageNumber(prev => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber(prev => Math.min(numPages, prev + 1));
  }, [numPages]);

  const zoomIn = useCallback(() => {
    setScale(prev => Math.min(PDF_VIEWER_CONFIG.ZOOM_MAX, prev + PDF_VIEWER_CONFIG.ZOOM_STEP));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => Math.max(PDF_VIEWER_CONFIG.ZOOM_MIN, prev - PDF_VIEWER_CONFIG.ZOOM_STEP));
  }, []);

  const resetZoom = useCallback(() => {
    setScale(PDF_VIEWER_CONFIG.DEFAULT_SCALE);
  }, []);

  const rotate = useCallback(() => {
    setRotation(prev => (prev + PDF_VIEWER_CONFIG.ROTATION_STEP) % 360);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const { getAccessTokenSilently } = useAuth0();
  const [fileSource, setFileSource] = useState<any>(null);

  // Build an authenticated file source for react-pdf
  useEffect(() => {
    let isMounted = true;
    const buildFileSource = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email"
          }
        });
        if (!isMounted) return;
        setFileSource({
          url,
          httpHeaders: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/pdf'
          },
        });
      } catch (e) {
        // Fallback to unauthenticated URL if token retrieval fails
        if (!isMounted) return;
        setFileSource({ url });
      }
    };
    buildFileSource();
    return () => {
      isMounted = false;
    };
  }, [url, getAccessTokenSilently]);

  const downloadPDF = useCallback(async () => {
    setIsDownloading(true);
    try {
      // Get auth token
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email"
        }
      });

      // Fetch the file with auth headers
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf',
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Could show a toast notification here
      alert('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [url, filename, getAccessTokenSilently]);

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 border border-red-200 rounded-lg bg-red-50 dark:bg-red-900/20 dark:border-red-800 ${className}`}>
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">
          PDF Loading Error
        </h3>
        <p className="text-sm text-red-600 dark:text-red-300 text-center">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className={`pdf-viewer ${isFullscreen ? 'fixed inset-0 z-50 bg-white dark:bg-gray-900' : ''} ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center space-x-2">
          {/* Page Navigation */}
          <div className="flex items-center space-x-1">
            <button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center space-x-1 px-2 text-sm">
              <input
                type="number"
                min={1}
                max={numPages}
                value={pageNumber}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= numPages) {
                    setPageNumber(page);
                  }
                }}
                className="w-12 text-center border rounded px-1 py-0.5 text-xs dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-gray-500 dark:text-gray-400">of {numPages}</span>
            </div>
            <button
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center space-x-1 border-l border-gray-300 dark:border-gray-600 pl-2">
            <button
              onClick={zoomOut}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={resetZoom}
              className="px-2 py-1 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-700 min-w-[3rem]"
              title="Reset zoom"
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              onClick={zoomIn}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>

          {/* Rotate Control */}
          <div className="flex items-center border-l border-gray-300 dark:border-gray-600 pl-2">
            <button
              onClick={rotate}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Rotate"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={toggleFullscreen}
            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            <Maximize className="w-4 h-4" />
          </button>
          <button
            onClick={downloadPDF}
            disabled={isDownloading}
            className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={isDownloading ? "Downloading..." : "Download PDF"}
          >
            {isDownloading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* PDF Content */}
      <div className={`pdf-content overflow-auto ${isFullscreen ? PDF_VIEWER_CONFIG.FULLSCREEN_HEIGHT : PDF_VIEWER_CONFIG.DEFAULT_HEIGHT} bg-gray-100 dark:bg-gray-900`}>
        {!fileSource ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center">
              <Loader className="w-8 h-8 animate-spin text-blue-500 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading PDF...</p>
            </div>
          </div>
        ) : (
        
        <div className="flex justify-center p-4">
          <Document
            file={fileSource}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading=""
            error=""
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              rotate={rotation}
              renderTextLayer={PDF_VIEWER_CONFIG.ENABLE_TEXT_LAYER}
              renderAnnotationLayer={enableAnnotations}
              className="shadow-lg"
            />
          </Document>
        </div>
        )}
      </div>

      {/* Footer */}
      {!isLoading && !error && (
        <div className="flex items-center justify-between p-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>{filename}</span>
          </div>
          <div>
            Page {pageNumber} of {numPages} • Scale: {Math.round(scale * 100)}%
          </div>
        </div>
      )}
    </div>
  );
};